import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { of, type Observable } from 'rxjs';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { NotificationStatus, UserNotification } from '../db/UserNotification.js';

/**
 * The live questions of one request, for the request view — regardless of who forwarded the
 * mail.
 *
 * Deliberately not filtered by recipient, unlike `subscribeMyNotifications`. In the request
 * view the questions are a property of the request, not of the viewer: someone opening a
 * colleague's blocked request has to see why it is stuck, otherwise the block is invisible to
 * everyone but the one person who is not there. Reading a request is open to every logged-in
 * user already (`server/startup.ts` grants blanket CRUD on `MailProcessing`), so this exposes
 * nothing that was not readable before.
 */
export const subscribeRequestNotifications = registerSubscription({
  type: () => [UserNotification],
  operationName: 'subscribeRequestNotifications',
  resolve: (mailID: string) => {
    return (_gqlFields: BaseQLSelectionSet<UserNotification>): Observable<UserNotification[]> => {
      if (!getCurrentUserID()) return of([]);

      return watchQuery<UserNotification>(
        UserNotification._collectionName,
        { mailID, status: { $in: [NotificationStatus.OPEN, NotificationStatus.ANSWERED] } },
        { sort: { createdAt: 1 } },
      );
    };
  },
  params: [{ name: 'mailID', type: () => String }],
});
